import React, { useState, useMemo } from "react";
import { Modal, Empty, Spin } from "antd";
import { ChatMessage, ChatItem } from "../../types/chat";
import { useGetChats } from "../../hooks/queries/chat";
import SearchInput from "../SearchInput";
import MessagePreview from "./MessagePreview";
import styles from "./ShareMessageModal.module.scss";
import ChatRoomItem from "./ChatRoomItem";

interface ShareMessageModalProps {
  message: ChatMessage | null;
  isOpen: boolean;
  onClose: () => void;
  currentRoomId?: string; // Current room to exclude from list
}

// Helper function to remove Vietnamese tones for search
const removeVietnameseTones = (str: string): string => {
  if (!str) return "";

  str = str.toLowerCase();
  str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, "a");
  str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, "e");
  str = str.replace(/ì|í|ị|ỉ|ĩ/g, "i");
  str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, "o");
  str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, "u");
  str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, "y");
  str = str.replace(/đ/g, "d");

  return str;
};

const ShareMessageModal: React.FC<ShareMessageModalProps> = ({ message, isOpen, onClose, currentRoomId }) => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedChats, setSelectedChats] = useState<ChatItem[]>([]);
  const [isSharing, setIsSharing] = useState(false);

  // Fetch available chat rooms
  const { data: chats = [], isLoading } = useGetChats({ keyword: "" });

  // Filter chats based on search and exclude current room
  const filteredChats = useMemo(() => {
    let filtered = chats;

    // Exclude current room
    if (currentRoomId) {
      filtered = filtered.filter((chat) => chat.ID !== currentRoomId);
    }

    // Apply search filter
    if (searchValue) {
      const normalizedSearch = removeVietnameseTones(searchValue);
      filtered = filtered.filter((chat) => removeVietnameseTones(chat.RoomName || "").includes(normalizedSearch));
    }

    return filtered;
  }, [searchValue, chats, currentRoomId]);

  // Handle chat selection toggle
  const handleToggleChat = (chat: ChatItem) => {
    setSelectedChats((prev) => {
      const isSelected = prev.some((c) => c.ID === chat.ID);
      if (isSelected) {
        return prev.filter((c) => c.ID !== chat.ID);
      } else {
        return [...prev, chat];
      }
    });
  };

  // Handle share action
  const handleShare = async () => {
    if (!message || selectedChats.length === 0) return;

    setIsSharing(true);

    // Import the share logic
    const { default: antdMessage } = await import("antd/es/message");
    const { chatApi } = await import("../../api/chat");

    let successCount = 0;
    let errorCount = 0;

    const contentToSend = message.content?.message || "";
    const metadataToSend = message.metadata && Object.keys(message.metadata).length > 0 ? message.metadata : undefined;

    // Share to each selected chat
    for (const chat of selectedChats) {
      if (!chat.ID) {
        errorCount++;
        continue;
      }

      try {
        await chatApi.sendMessage(chat.ID, contentToSend, metadataToSend);
        successCount++;
      } catch (error) {
        console.error(`Failed to share to ${chat.RoomName}:`, error);
        errorCount++;
      }
    }

    setIsSharing(false);

    // Show result message
    if (successCount > 0 && errorCount === 0) {
      antdMessage.success(`Đã chia sẻ tin nhắn đến ${successCount} cuộc trò chuyện.`);
    } else if (successCount > 0 && errorCount > 0) {
      antdMessage.warning(`Chia sẻ thành công đến ${successCount} và thất bại đến ${errorCount} cuộc trò chuyện.`);
    } else {
      antdMessage.error("Không thể chia sẻ tin nhắn. Vui lòng thử lại.");
    }

    // Close modal and reset state
    handleClose();
  };

  // Handle modal close
  const handleClose = () => {
    setSearchValue("");
    setSelectedChats([]);
    setIsSharing(false);
    onClose();
  };

  if (!message) return null;

  return (
    <Modal
      title="Chia sẻ tin nhắn"
      open={isOpen}
      onCancel={handleClose}
      onOk={handleShare}
      okText="Chia sẻ"
      cancelText="Hủy"
      okButtonProps={{
        disabled: selectedChats.length === 0,
        loading: isSharing,
      }}
      cancelButtonProps={{
        disabled: isSharing,
      }}
      width={600}
      className={styles.shareModal}
    >
      {/* Message Preview */}
      <div className={styles.previewSection}>
        <MessagePreview message={message} />
      </div>

      {/* Search Input */}
      <SearchInput
        placeholder="Tìm kiếm cuộc trò chuyện..."
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
      />

      {/* Chat List */}
      <div className={styles.chatList}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spin />
          </div>
        ) : filteredChats.length === 0 ? (
          <Empty description="Không tìm thấy cuộc trò chuyện" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          filteredChats.map((chat) => (
            <ChatRoomItem
              key={chat.ID}
              chat={chat}
              isSelected={selectedChats.some((c) => c.ID === chat.ID)}
              onToggle={() => handleToggleChat(chat)}
            />
          ))
        )}
      </div>
    </Modal>
  );
};

export default ShareMessageModal;
